import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
} from '@backstage/core-components';
import { RBACSidebarItem } from '@spotify/backstage-plugin-rbac';

import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import AppsIcon from '@material-ui/icons/Apps';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import MapIcon from '@material-ui/icons/MyLocation';
import TokenIcon from '@material-ui/icons/ConfirmationNumber';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import BuildIcon from '@material-ui/icons/Build';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import DynamicFeedIcon from '@material-ui/icons/DynamicFeed';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import SpeedIcon from '@material-ui/icons/Speed';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import PollIcon from '@material-ui/icons/Poll';
import SettingsIcon from '@material-ui/icons/Settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import LogoIcon from './LogoIcon';
import { GraphiQLIcon } from '@zeiss/plugin-zeiss-graphiql';

import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  frontendGraphiqlPermission,
  frontendSkillExchangePermission,
  frontendTokenPermission,
  frontendVersionDashboardPermission,
} from '@zeiss/backstage-plugin-frontend-permissions-common';
import { ZeissFeatureFlagged } from '@zeiss/backstage-plugin-feature-flags';
import { qetaCreatePostPermission } from '@drodil/backstage-plugin-qeta-common';

import { InsightsSidebarItem } from '@spotify/backstage-plugin-insights';
import SchoolIcon from '@material-ui/icons/School';
import { updateCheckAnalysisPermission } from '@zeiss/plugin-update-check-common';
import { BeyondDesignSystemRouterPath } from '@zeiss/backstage-plugin-design-system';

const useSidebarItemStyles = makeStyles({
  item: {
    '& span': {
      whiteSpace: 'normal',
    },
  },
});

const useHeaderStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1300,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 36px',
    backgroundColor: 'var(--global-background-inverse)',
    color: 'var(--global-text-on-color)',
    height: '40px',
    fontSize: '16px',
    fontWeight: 700,
  },
  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 36px',
    backgroundColor: 'var(--global-background-primary)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
});

const useSidebarPageStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  headerSpacer: {
    height: '120px',
    flexShrink: 0,
  },
});

const useSidebarStyles = makeStyles({
  sidebarWrapper: {
    flex: 1,
    minHeight: 0,
    marginLeft: 16,
    overflow: 'auto',
    display: 'flex',
    // stable data attribute — works on prod
    '& [data-backstage-core-page]': {
      overflow: 'visible',
      minHeight: 'unset',
      padding: 0,
      boxSizing: 'border-box',
    },
  },
});

const useContentStyles = makeStyles({
  contentWrapper: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    paddingLeft: 40,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  innerContent: {
    flex: 1,
    paddingRight: 40,
  },
});

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const config = useApi(configApiRef);
  const classes = useSidebarItemStyles();
  const contentClasses = useContentStyles();
  const headerClasses = useHeaderStyles();
  const sidebarClasses = useSidebarStyles();
  const sidebarPageClasses = useSidebarPageStyles();
  const isUpdateCheckActivated: boolean | undefined = config.getOptionalBoolean(
    'updatecheck.activated',
  );

  return (
    <div className={sidebarPageClasses.root}>
      <header className={headerClasses.header}>
        <div className={headerClasses.topBar}>
          <span>ZEISS Developer Portal</span>
        </div>
        <div className={headerClasses.bottomBar}>
          <div className={headerClasses.logoContainer}>
            <LogoIcon />
          </div>
        </div>
      </header>

      <div className={sidebarPageClasses.headerSpacer} />

      <div className={sidebarClasses.sidebarWrapper}>
        <SidebarPage>
          <Sidebar>
            <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
              <SidebarSearchModal />
            </SidebarGroup>

            <SidebarItem icon={CreateComponentIcon} to="create" text="Create" />

            <SidebarDivider />

            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarScrollWrapper>
                <SidebarItem icon={HomeIcon} to="/" text="Home" />
                <SidebarItem icon={AppsIcon} to="catalog" text="App Catalog" />
                <SidebarItem
                  icon={CategoryOutlinedIcon}
                  to={`${BeyondDesignSystemRouterPath}`}
                  className={classes.item}
                  text="Beyond Design System"
                />
                <SidebarItem icon={DeviceHubIcon} to="/dees" text="DEES" />
                <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
                <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
                <SidebarItem
                  icon={MapIcon}
                  to="tech-radar"
                  text="Tech Radars"
                />
                <RequirePermission
                  permission={frontendSkillExchangePermission}
                  errorPage={<></>}
                >
                  <SidebarItem
                    icon={SchoolIcon}
                    to="skill-exchange"
                    text="Skill Exchange"
                  />
                </RequirePermission>
                <RBACSidebarItem />
                <InsightsSidebarItem />
                <SidebarItem
                  icon={DoneAllIcon}
                  to="soundcheck"
                  text="Soundcheck"
                />
                {isUpdateCheckActivated && (
                  <RequirePermission
                    permission={updateCheckAnalysisPermission}
                    errorPage={<></>}
                  >
                    <SidebarItem
                      icon={PollIcon}
                      to="update-check-analysis"
                      text="Libs Analysis"
                    />
                  </RequirePermission>
                )}

                <SidebarDivider />

                <RequirePermission
                  permission={qetaCreatePostPermission}
                  errorPage={<></>}
                >
                  <SidebarItem icon={LiveHelpIcon} to="qeta" text="Community" />
                </RequirePermission>

                <SidebarDivider />

                <RequirePermission
                  permission={frontendTokenPermission}
                  errorPage={<></>}
                >
                  <SidebarItem
                    icon={TokenIcon}
                    text="Token Retrieval"
                    to="/token-retrieval"
                  />
                </RequirePermission>
                <RequirePermission
                  permission={frontendGraphiqlPermission}
                  errorPage={<></>}
                >
                  <SidebarItem
                    icon={GraphiQLIcon}
                    text="GraphiQL"
                    to="/graphiql"
                  />
                </RequirePermission>
                <RequirePermission
                  permission={frontendVersionDashboardPermission}
                  errorPage={<></>}
                >
                  <SidebarItem
                    icon={DynamicFeedIcon}
                    to="version-dashboard"
                    text="Deployments"
                  />
                </RequirePermission>
                <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
                <SidebarItem
                  icon={AssignmentTurnedInIcon}
                  to="entity-linter"
                  text="Entity Linter"
                />
                <ZeissFeatureFlagged name="lighthouse">
                  <SidebarItem
                    icon={SpeedIcon}
                    to="lighthouse"
                    text="Lighthouse"
                  />
                </ZeissFeatureFlagged>

                <SidebarDivider />

                <NotificationsSidebarItem />

                <SidebarItem
                  icon={SettingsIcon}
                  to="/settings"
                  text="Settings"
                />
              </SidebarScrollWrapper>
            </SidebarGroup>
          </Sidebar>
          <div className={contentClasses.contentWrapper}>
            <div className={contentClasses.innerContent}>
              {children}
            </div>
          </div>
        </SidebarPage>
      </div>
    </div>
  );
};